// ═══════════════════════════════════════════════════════════════
// MainStreet — Create Organization + Bulk Upload
// ═══════════════════════════════════════════════════════════════

import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Button, TextInput, Select, Textarea,
  Tabs, SimpleGrid, Stack, FileButton, Progress,
} from '../../components/gab/GabComponents';
import {
  IconArrowLeft, IconPlus, IconUpload, IconCheck,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import Papa from 'papaparse';
import { useMainStreetStore } from '../../store/mainstreetStore';
import { SECTOR_TYPES, OP_STATUS } from './constants';

const SECTOR_SELECT = SECTOR_TYPES.map(s => ({ value: s.id, label: s.label }));
const STATUS_SELECT = OP_STATUS.map(s => ({ value: s.id, label: s.label }));
const INSURANCE_OPTIONS = [
  { value: 'unknown', label: 'Unknown' },
  { value: 'insured', label: 'Insured' },
  { value: 'partial', label: 'Partial' },
  { value: 'uninsured', label: 'Uninsured' },
];
const CONTINUITY_OPTIONS = [
  { value: 'unknown', label: 'Unknown' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const EMPTY_FORM = {
  name: '', sectorType: '', address: '', city: '', state: '', zip: '',
  phone: '', email: '', primaryContact: '', opStatus: '', insurance: '',
  continuity: '', notes: '',
};

export default function OrgCreate() {
  const navigate = useNavigate();
  const { createOrganization } = useMainStreetStore();

  // ── Single add ──────────────────────────────────────────────
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  const setField = (key: string, value: string) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleCreate = async () => {
    if (!form.name.trim()) {
      notifications.show({ message: 'Organization name is required', color: 'yellow' });
      return;
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      notifications.show({ message: 'Please enter a valid email address', color: 'yellow' });
      return;
    }
    if (form.phone && !/^[\d\s().+-]{7,}$/.test(form.phone)) {
      notifications.show({ message: 'Please enter a valid phone number', color: 'yellow' });
      return;
    }
    if (form.zip && !/^\d{5}(-\d{4})?$/.test(form.zip)) {
      notifications.show({ message: 'Please enter a valid ZIP code', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      const org = await createOrganization({
        ...form,
        lastVerified: new Date().toISOString(),
      });
      notifications.show({ message: `${form.name} created`, color: 'green' });
      setForm({ ...EMPTY_FORM });
      if (org) {
        navigate(`/mainstreet/org/${org.id}`);
      }
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Create failed', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  // ── Bulk upload ─────────────────────────────────────────────
  const [csvRows, setCsvRows] = useState<Record<string, string>[]>([]);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importTotal, setImportTotal] = useState(0);
  const resetRef = useRef<() => void>(null);

  const handleFileSelect = (file: File | null) => {
    if (!file) return;
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setCsvRows(results.data);
        notifications.show({ message: `Parsed ${results.data.length} rows`, color: 'blue' });
      },
      error: () => {
        notifications.show({ message: 'Failed to parse CSV', color: 'red' });
      },
    });
  };

  const handleImport = async () => {
    if (csvRows.length === 0) return;
    setImporting(true);
    setImportTotal(csvRows.length);
    setImportProgress(0);
    let success = 0;
    let failed = 0;
    for (let i = 0; i < csvRows.length; i++) {
      const row = csvRows[i];
      const name = (row.name || '').trim();
      if (!name) { failed++; setImportProgress(i + 1); continue; }
      try {
        await createOrganization({
          name,
          sectorType: (row.sectorType || row.sector || '').trim(),
          address: (row.address || '').trim(),
          city: (row.city || '').trim(),
          state: (row.state || '').trim(),
          zip: (row.zip || '').trim(),
          phone: (row.phone || '').trim(),
          email: (row.email || '').trim(),
          primaryContact: (row.primaryContact || row.contact || '').trim(),
          lastVerified: new Date().toISOString(),
        });
        success++;
      } catch {
        failed++;
      }
      setImportProgress(i + 1);
    }
    setImporting(false);
    notifications.show({
      message: `Imported ${success} organizations${failed > 0 ? `, ${failed} failed` : ''}`,
      color: failed > 0 ? 'yellow' : 'green',
    });
    setCsvRows([]);
    resetRef.current?.();
  };

  return (
    <div>
      <Group mb="md">
        <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />}
          onClick={() => navigate('/mainstreet/organizations')}>Back</Button>
        <Title order={3} c="black">Add Organization</Title>
      </Group>

      <Tabs defaultValue="single">
        <Tabs.List mb="md">
          <Tabs.Tab value="single" leftSection={<IconPlus size={14} />}>Add Organization</Tabs.Tab>
          <Tabs.Tab value="bulk" leftSection={<IconUpload size={14} />}>Bulk Upload</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="single">
          <Paper withBorder p="md">
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
              <TextInput label="Name" required value={form.name} onChange={e => setField('name', e.target.value)} />
              <Select label="Sector" data={SECTOR_SELECT} value={form.sectorType || null} onChange={v => setField('sectorType', v ?? '')} clearable />
              <TextInput label="Address" value={form.address} onChange={e => setField('address', e.target.value)} />
              <TextInput label="City" value={form.city} onChange={e => setField('city', e.target.value)} />
              <TextInput label="State" value={form.state} onChange={e => setField('state', e.target.value)} />
              <TextInput label="ZIP" value={form.zip} onChange={e => setField('zip', e.target.value)} />
              <TextInput label="Phone" value={form.phone} onChange={e => setField('phone', e.target.value)} />
              <TextInput label="Email" value={form.email} onChange={e => setField('email', e.target.value)} />
              <TextInput label="Primary Contact" value={form.primaryContact} onChange={e => setField('primaryContact', e.target.value)} />
              <Select label="Operational Status" data={STATUS_SELECT} value={form.opStatus || null} onChange={v => setField('opStatus', v ?? '')} clearable />
              <Select label="Insurance" data={INSURANCE_OPTIONS} value={form.insurance || null} onChange={v => setField('insurance', v ?? '')} clearable />
              <Select label="Continuity Plan" data={CONTINUITY_OPTIONS} value={form.continuity || null} onChange={v => setField('continuity', v ?? '')} clearable />
              <Textarea label="Notes" value={form.notes} onChange={e => setField('notes', e.target.value)} autosize minRows={2} style={{ gridColumn: '1 / -1' }} />
            </SimpleGrid>
            <Group justify="flex-end" mt="md">
              <Button leftSection={<IconCheck size={14} />} loading={saving} onClick={handleCreate}>
                Create Organization
              </Button>
            </Group>
          </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="bulk">
          <Paper withBorder p="md">
            <Stack>
              <Text size="sm" c="dimmed">
                Upload a CSV file with columns: <strong>name, sectorType, address, city, state, zip, phone, email, primaryContact</strong>
              </Text>
              <Group>
                <FileButton resetRef={resetRef} onChange={handleFileSelect} accept=".csv">
                  {(props) => <Button variant="light" leftSection={<IconUpload size={14} />} {...props}>Select CSV</Button>}
                </FileButton>
                {csvRows.length > 0 && (
                  <Text size="sm">{csvRows.length} rows ready</Text>
                )}
              </Group>

              {csvRows.length > 0 && (
                <>
                  <Text size="xs" fw={500}>Preview (first 5 rows):</Text>
                  <Paper withBorder style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                          {Object.keys(csvRows[0]).map(key => (
                            <th key={key} style={{ padding: '6px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>{key}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {csvRows.slice(0, 5).map((row, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
                            {Object.values(row).map((val, j) => (
                              <td key={j} style={{ padding: '6px 8px', whiteSpace: 'nowrap', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{val}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Paper>

                  {importing && (
                    <div>
                      <Text size="xs" mb={4}>{importProgress} / {importTotal}</Text>
                      <Progress value={(importProgress / importTotal) * 100} size="sm" />
                    </div>
                  )}

                  <Group>
                    <Button leftSection={<IconUpload size={14} />} loading={importing} onClick={handleImport}>
                      Import {csvRows.length} Organizations
                    </Button>
                    <Button variant="subtle" color="gray" onClick={() => { setCsvRows([]); resetRef.current?.(); }}>
                      Clear
                    </Button>
                  </Group>
                </>
              )}
            </Stack>
          </Paper>
        </Tabs.Panel>
      </Tabs>
    </div>
  );
}
